import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { assessmentApi } from '../../services/assessmentApi';

export const assessmentKeys = {
  all: ['assessments'] as const,
  lists: () => [...assessmentKeys.all, 'list'] as const,
  list: (filters?: { pageNumber?: number; pageSize?: number; search?: string }) =>
    [...assessmentKeys.lists(), filters] as const,
  details: () => [...assessmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...assessmentKeys.details(), id] as const,
  registryTerms: () => [...assessmentKeys.all, 'registry-terms'] as const,
  rules: () => [...assessmentKeys.all, 'rules'] as const,
};

export function useGetAssessment(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: assessmentKeys.detail(id),
    queryFn: async () => {
      const response = await assessmentApi.getById(id);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!id,
  });
}

export function useGetAssessments() {
  return useQuery({
    queryKey: assessmentKeys.lists(),
    queryFn: async () => {
      const response = await assessmentApi.getList();
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessments'}`);
      }
      return response;
    },
  });
}

export function useGetAssessmentsPaged(
  pageNumber: number = 1,
  pageSize: number = 10,
  search?: string
) {
  return useQuery({
    queryKey: assessmentKeys.list({ pageNumber, pageSize, search }),
    queryFn: async () => {
      const response = await assessmentApi.getPagedList(pageNumber, pageSize, search);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessments'}`);
      }
      return response;
    },
  });
}

export function useGetRegistryTerms(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: assessmentKeys.registryTerms(),
    queryFn: async () => {
      const response = await assessmentApi.getRegistryTerms();
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch registry terms'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useGetAssessmentRules(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: assessmentKeys.rules(),
    queryFn: async () => {
      const response = await assessmentApi.getRules();
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch assessment rules'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? true,
  });
}
